import { createFileRoute, Link } from "@tanstack/react-router";
import { sectionImageMeta, ogUrl } from "@/lib/ogSections";
import { motion } from "motion/react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight, ClipboardList, Layers, Cog, ShieldCheck } from "lucide-react";
import solPerimeter from "@/assets/sol-perimeter.jpg";
import solCinema from "@/assets/sol-cinema.jpg";
import solSecurity from "@/assets/sol-security.jpg";
import solAuditorium from "@/assets/sol-auditorium.jpg";
import solAutomation from "@/assets/sol-automation.jpg";
import solEnterprise from "@/assets/sol-enterprise.jpg";
import portraitProcess from "@/assets/portrait-process.webp";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript } from "@/seo/schema";


const PROCESS = [
  { Icon: ClipboardList, step: "01", title: "Understanding Requirements", body: "Site context, risk model, user expectation and operational priorities — captured before a single product is named." },
  { Icon: Layers, step: "02", title: "Technology Architecture & Design", body: "Vendor-neutral system design — sensors, networks, control fabric and AV stack engineered as one." },
  { Icon: Cog, step: "03", title: "System Integration & Execution", body: "Disciplined deployment across trades — cabling, devices, controllers and software commissioned to spec." },
  { Icon: ShieldCheck, step: "04", title: "Performance Validation & Delivery", body: "Acceptance testing, training and SOPs — handover only after measurable performance is proven." },
];

// Representative project categories — not individual case studies.
const CATEGORIES = [
  {
    tag: "Perimeter",
    title: "Multi-Kilometre AI Perimeter Programs",
    region: "North India · Mumbai · Bengaluru",
    note: "Logistics, industrial and critical-infrastructure sites with radar, fibre and thermal sensor fusion.",
    image: solPerimeter,
  },
  {
    tag: "Residence",
    title: "Luxury Estate — Automation & Private Cinema",
    region: "New Delhi · Noida · Gurugram",
    note: "Integrated lighting, climate, security and Dolby Atmos reference cinema in private residences.",
    image: solCinema,
  },
  {
    tag: "Government",
    title: "Critical Infrastructure Security Grids",
    region: "North India · Mumbai · Bengaluru",
    note: "Layered detection, command-and-control and AI analytics for national infrastructure operators.",
    image: solSecurity,
  },
  {
    tag: "Auditorium",
    title: "Institutional & Campus Auditoriums",
    region: "Noida · Ghaziabad · North India",
    note: "Acoustically engineered AV programs for university and corporate auditoriums.",
    image: solAuditorium,
  },
  {
    tag: "Smart Home",
    title: "KNX & Crestron Integrated Residences",
    region: "New Delhi · Noida · Gurugram",
    note: "Vendor-neutral control fabric for luxury homes — lighting, shades, climate, AV and security.",
    image: solAutomation,
  },
  {
    tag: "Enterprise",
    title: "Boardrooms & Hybrid Meeting Estates",
    region: "Pan India · Global",
    note: "Standardised UCC and AV across portfolios — Microsoft Teams, Zoom and bespoke rooms.",
    image: solEnterprise,
  },
];

const PROJECTS_TITLE = "Project Categories — Security, Automation, AV | Arif Khan";
const PROJECTS_DESC =
  "Representative categories of security, perimeter, automation, luxury cinema and auditorium AV programs led by Arif Khan.";

export const Route = createFileRoute("/projects")({
  component: ProjectsPage,
  head: () => ({
    meta: [
      { title: PROJECTS_TITLE },
      { name: "description", content: PROJECTS_DESC },

      { property: "og:title", content: "Project Categories | Arif Khan" },
      { property: "og:description", content: "Representative categories of security, perimeter, automation, luxury cinema and auditorium AV programs led by Arif Khan." },
      { property: "og:url", content: "https://arifkhanglobal.com/projects" },
      ...sectionImageMeta(ogUrl(solPerimeter), "Selected projects — Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/projects" },
      ...hreflangLinks("/projects"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/projects.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/projects",
          type: "CollectionPage",
          name: PROJECTS_TITLE,
          description: PROJECTS_DESC,
          breadcrumb: [{ name: "Projects", url: "/projects" }],
        },
        [
          {
            "@type": "ItemList",
            "@id": "https://arifkhanglobal.com/projects#list",
            name: "Project Categories",
            numberOfItems: CATEGORIES.length,
            itemListElement: CATEGORIES.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "CreativeWork",
                name: c.title,
                about: c.tag,
                description: c.note,
                locationCreated: c.region,
              },
            })),
          },
        ],
      ),
    ],


  }),
});

function ProjectsPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Representative Work"
        title="Project categories across 23+ years."
        description="These are representative categories of programs Arif Khan has led across India. Client names and individual case studies are shared confidentially on request."
      />
      <section className="py-24">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <article className="group rounded-2xl border border-border overflow-hidden bg-background hover:border-border-strong transition-all h-full flex flex-col">
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.title} — representative ${p.tag.toLowerCase()} project`}
                    width={1200}
                    height={1500}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 scrim-bottom" />
                  <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                    <span className="glass-dark rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white">{p.tag}</span>
                    <ArrowUpRight className="h-5 w-5 text-white/90 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-5 text-xs font-mono text-white/80">Representative · {p.region}</div>
                </div>
                <div className="p-6 flex-1">
                  <h3 className="font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{p.note}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="container-page mt-16 text-center">
          <p className="text-sm text-ink-soft max-w-xl mx-auto mb-6">
            For verified case studies, references and site visits, please request access through a private consultation.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 text-primary font-medium">
            Let's Talk <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <ProcessSection />
    </PageShell>
  );
}

function ProcessSection() {
  return (
    <section className="py-32 bg-surface border-t border-border overflow-hidden">
      <div className="container-page">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow">The Method</span>
          <h2 className="mt-4 text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            How projects move from <span className="text-primary">concept</span> to execution.
          </h2>
          <p className="mt-5 text-pretty text-base md:text-lg text-ink-soft leading-relaxed">
            A disciplined four-stage framework — applied to every engagement, from kilometre-scale perimeter programs to private cinema suites.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — portrait */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative">
              <div aria-hidden className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-primary/25 via-primary/5 to-accent/15 blur-2xl opacity-80" />
              <div className="relative overflow-hidden rounded-[1.5rem] glass border border-white/40 shadow-[0_30px_80px_-40px_rgba(21,101,192,0.5)] p-2">
                <div className="relative overflow-hidden rounded-[1.15rem] aspect-[4/5]">
                  <img
                    src={portraitProcess}
                    alt="Arif Khan reviewing project technical drawings"
                    width={1200}
                    height={1500}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-[30%_25%] scale-[1.05]"
                  />
                  {/* Background blur via radial vignette + warmer focus on subject */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_70%_at_30%_45%,transparent_30%,rgba(15,23,42,0.45)_100%)]" />
                  {/* Subtle blue accent */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/15 via-transparent to-transparent mix-blend-overlay" />
                  <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-background/85 via-background/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="text-sm font-semibold tracking-tight">Arif Khan</div>
                    <div className="text-xs text-ink-soft mt-0.5">Project planning · Architecture review</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — process cards */}
          <div className="lg:col-span-7 space-y-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="group relative rounded-2xl border border-border bg-background hover:border-border-strong hover:shadow-card transition-all duration-500 p-7 md:p-8">
                  <div className="flex items-start gap-6">
                    <div className="shrink-0">
                      <div className="font-mono text-[10px] text-ink-soft tracking-widest mb-2">STEP</div>
                      <div className="text-3xl md:text-4xl font-semibold tracking-tight text-primary tabular-nums">{p.step}</div>
                    </div>
                    <div className="hidden md:block h-16 w-px bg-border" aria-hidden />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-primary">
                          <p.Icon className="h-4 w-4" strokeWidth={1.6} />
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold tracking-tight">{p.title}</h3>
                      </div>
                      <p className="text-sm md:text-[15px] text-ink-soft leading-relaxed">{p.body}</p>
                    </div>
                    <ArrowUpRight className="hidden md:block h-4 w-4 text-ink-soft/40 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
